import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Stack,
    Box,
    Tooltip
} from '@mui/material';
import { When } from 'react-if';
import { NoData } from '../../../../components';
import {DefaultRes, ReportTypes} from "../../../../types";
import {sexOffendersTableColumns} from "./sex-offenders-table-columns";
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import Loading from "../../../../components/loading";
import {api} from "../../../../http";
import {AxiosResponse} from "axios";
import {capitalizeFirstLetter} from "../../../../helpers/crimeScore";

interface Props {
    crimeScore: number;
    sexOffenders: ReportTypes.SexOffender[];
    sexOffendersWithin3M: number | string;
    passedInNearbyReports?: ReportTypes.NearbyReport[] | null;
    address : string;
    place ?: ReportTypes.Place
}

const columns = sexOffendersTableColumns;

const getDataValue = (field:string,data:ReportTypes.SexOffender | null): string =>{
    if(!data) return "N/A";
    let value : any = data[field]
    if(field === 'fullName')
        value = data.sex_offender_first_name ? `${data?.sex_offender_first_name} ${data?.sex_offender_middle_name} ${data?.sex_offender_last_name}` : 'N/A'
    return value?capitalizeFirstLetter(value?.toString()):"N/A";
}


export const SexOffendersInfo: React.FC<Props> = ({place, sexOffenders, address, sexOffendersWithin3M, passedInNearbyReports}) => {
    const data : ReportTypes.SexOffender | null = sexOffenders?sexOffenders[0]:null

    //create react state for nearbyReports with initial value from the props
    const [nearbyReports, setNearbyReports] = useState<ReportTypes.NearbyReport[] | null>(passedInNearbyReports!);
    const [isLoading, setIsLoading] = useState<boolean>(false);

   useEffect(() => {
        if(!place || (passedInNearbyReports && passedInNearbyReports.length > 0))
            return;

        const fetchNearbyReports = async () => {
            setIsLoading(true);
           try {
                const response = await api.post<DefaultRes, AxiosResponse<DefaultRes>>('reports/getNearbyReports', { place });
                const { result, isValid } = response.data;
                if (isValid && result)
                    setNearbyReports(result);
            }finally {
                setIsLoading(false);
            }
        };
        fetchNearbyReports();
    }, []);

    return (
        <Stack mt={4}>
            {/****
             sex offenders table data section
             */}
            <Box
                sx={{
                    marginBottom: 2,
                    display:'flex',
                    flexDirection:'row'
                }}
            >
                <Typography fontSize={14} fontWeight={500} lineHeight={"24px"}>
                    Sex Offenders At or within 160ft of {address}
                </Typography>
                <When condition={sexOffenders?.length}>
                    <Box sx={{
                        marginLeft:2,
                        height: 16,
                        minWidth: 16,
                        lineHeight: 0.5,
                        borderRadius: 8,
                        cursor: 'default',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        display: 'inline-flex',
                        justifyContent: 'center',
                        padding:'10px 6px',
                        position:'relative',
                        backgroundColor : '#FF0000',
                        color: "primary.contrastText"
                    }}>
                        <Typography fontSize={12} fontWeight={600} className={'text-download-adjust'}>
                            High Risk
                        </Typography>
                    </Box>
                </When>
            </Box>
            <Card sx={{marginBottom : 2}}>
                {sexOffenders?.length > 0 &&
                <Box className={'div-border-for-download'} sx={{display: "flex", flexWrap: 'wrap', justifyContent: "space-around"}}>
                    {columns?.map((col, colKey) => (
                        <TableContainer sx={{width: {md: col.width, xs: 130}}} key={colKey}>
                            <Table sx={{mb: 2}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{backgroundColor:'#11A7E2'}}>
                                            <Typography
                                                variant="body2"
                                                sx={{color: 'primary.contrastText'}}
                                            >
                                                {col.headerName}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{wordBreak:'break-word'}}>
                                            <Box sx={{display:'flex',gap:'5px',alignSelf:'flex-end',flexDirection:'row'}}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{color: 'text.secondary'}}
                                                >
                                                    {getDataValue(col.field, data)}
                                                </Typography>
                                                <When condition={col.isAlert}>
                                                    <Typography
                                                        sx={{color:'error.main'}}
                                                    >
                                                        <When condition={col.tooltipText && col.tooltipText.length > 0}>
                                                            <Tooltip title={col.tooltipText} placement={'right'}>
                                                                <ReportProblemIcon/>
                                                            </Tooltip>
                                                        </When>
                                                        <When condition={!col.tooltipText}>
                                                            <ReportProblemIcon/>
                                                        </When>
                                                    </Typography>
                                                </When>
                                            </Box>

                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ))}
                </Box>
                }
                <div className={'div-border-for-download'}>
                    <When condition={!sexOffenders || sexOffenders?.length === 0}>
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={'No records found'}
                            description={
                                'No sex offender resides at this location.'
                            }
                        />
                    </When>
                </div>
            </Card>

            {/****
             Nearby reports area
             */}
            <Box
                sx={{
                    marginBottom: 2,
                    marginTop : 2
                }}
            >
                <Typography fontSize={14} fontWeight={500} lineHeight={"24px"}>
                    Field Safety Reports At or within 160ft of {address}
                </Typography>
            </Box>
            <Card className={'div-border-for-download'}>
                <TableContainer>
                    <Table sx={{mb: 2}}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{backgroundColor:'#11A7E2'}} colSpan={3}>
                                    <Typography
                                        variant="body2"
                                        sx={{color: 'primary.contrastText'}}
                                    >
                                        Reports
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <When condition={isLoading}>
                            <Loading />
                        </When>
                        <When condition={!isLoading}>
                            <When condition={!nearbyReports || nearbyReports?.length === 0}>
                                <NoData
                                    buttonText={null}
                                    onClick={() => {}}
                                    title={'No records found'}
                                    description={
                                        'No nearby reports at this location.'
                                    }
                                />
                            </When>
                            <When condition={nearbyReports && nearbyReports.length > 0}>
                                <TableBody>
                                    {nearbyReports?.map((report,index)=>{
                                        return (
                                            <TableRow key={index} sx={{
                                                backgroundColor: (theme) =>
                                                    index % 2 === 0
                                                        ? theme.palette.common.white  // Even rows
                                                        : theme.palette.grey[200], // Odd rows

                                            }}>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{color: 'text.secondary'}}
                                                    >
                                                        {report.name ? report.name : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{color: 'text.secondary'}}
                                                    >
                                                        {report.reportComment ? report.reportComment : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{color: 'text.secondary'}}
                                                    >
                                                        {report.address ? report.address : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </When>
                        </When>
                    </Table>
                </TableContainer>
            </Card>


            {/****
             In the area section
             */}
            <Box
                sx={{
                    marginBottom: 2,
                    marginTop : 2
                }}
            >
                <Typography fontSize={14} fontWeight={500} lineHeight={"24px"}>
                    In The Area
                </Typography>
            </Box>
            <Card className={'div-border-for-download'}>
                <TableContainer>
                    <Table sx={{mb: 2}}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{backgroundColor:'#11A7E2'}}>
                                    <Typography
                                        variant="body2"
                                        sx={{color: 'primary.contrastText'}}
                                    >
                                        Sex Offenders in a 3 Mile radius
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        sx={{color: 'text.secondary'}}
                                    >
                                        {sexOffendersWithin3M ? sexOffendersWithin3M : 'N/A'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Stack>
    );
};
